import { useEffect, useMemo, useState } from "react";
import { Checkbox, Input, Modal, Select, Spin } from "antd";
import useNotification from "../../../components/notification/notification";
import ApiRoles from "../../../api/roles";
import { CONFIRM_MODAL, failureMessage, permissionDiff } from "./role-edit";

/**
 * LF360-2049 — deleting a custom role (draft, story 7).
 *
 * The admin types the role's name. When anybody holds the role, the dialog
 * lists them and asks for a replacement, shows what they gain and lose with
 * it, and makes the admin acknowledge a gain; a Whole project replacement is
 * warned about, since site links stop limiting them.
 */
const RoleDeleteModal = ({ open, role, roles, catalog, onCancel, onDeleted }) => {
  const { openNotification } = useNotification();
  const [members, setMembers] = useState(null);
  const [typed, setTyped] = useState("");
  const [replacementId, setReplacementId] = useState(null);
  const [understood, setUnderstood] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setMembers(null);
    setTyped("");
    setReplacementId(null);
    setUnderstood(false);
    ApiRoles.getRoleMembers(role.id)
      .then(({ data }) => setMembers(data?.members || []))
      .catch(() => {
        openNotification({ type: "error", message: <p>Unable to load the role's members</p> });
        onCancel();
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, role.id]);

  const replacement = roles.find((r) => r.id === replacementId) || null;
  const needsReplacement = Boolean(members?.length);
  const change = useMemo(
    () => (replacement ? permissionDiff(catalog, role.permissions, replacement.permissions) : null),
    [catalog, role.permissions, replacement]
  );
  const gains = Boolean(change?.added.length);

  const ready = members !== null
    && typed === role.name
    && (!needsReplacement || (replacement && (!gains || understood)));

  const confirm = async () => {
    setBusy(true);
    try {
      const { data } = await ApiRoles.deleteRole(role.id, {
        version: role.version,
        ...(needsReplacement ? { replacementRoleId: replacement.id } : {}),
      });
      openNotification({
        type: "success",
        message: (
          <p>
            {data.replacement
              ? `Deleted role "${role.name}". ${data.moved} users moved to "${data.replacement.name}"`
              : `Deleted role "${role.name}"`}
          </p>
        ),
      });
      onDeleted(data);
    } catch (error) {
      openNotification({ type: "error", message: <p>{failureMessage(error, "Unable to delete the role")}</p> });
      onCancel();
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open={open}
      title="Delete role"
      okText="Delete"
      okButtonProps={{ danger: true, disabled: !ready }}
      onOk={confirm}
      onCancel={onCancel}
      confirmLoading={busy}
      destroyOnHidden
      {...CONFIRM_MODAL}
    >
      {members === null ? (
        <Spin />
      ) : (
        <div className="role-delete">
          <p>Are you sure you want to delete the role "{role.name}"?</p>
          <p>Type the role name to confirm.</p>
          <Input
            aria-label="Role name"
            value={typed}
            onChange={(event) => setTyped(event.target.value)}
          />

          {needsReplacement && (
            <>
              <p className="role-delete__heading">Members and invitations</p>
              <ul className="role-delete__members">
                {members.map((member) => (
                  <li key={member.membershipId}>
                    {member.invited ? `Invitation: ${member.email || member.name}` : member.name}
                    {" · "}
                    {member.projectTitle}
                    {member.sites.length ? ` · ${member.sites.join(", ")}` : ""}
                  </li>
                ))}
              </ul>

              <p className="role-delete__heading">Replacement role</p>
              <Select
                aria-label="Replacement role"
                placeholder="Select replacement role…"
                value={replacementId}
                onChange={(value) => {
                  setReplacementId(value);
                  setUnderstood(false);
                }}
                options={roles.filter((r) => r.id !== role.id).map((r) => ({ value: r.id, label: r.name }))}
                style={{ width: "100%" }}
              />

              {change && (
                <div className="role-confirm">
                  {change.added.length > 0 && <p>Gained: {change.added.join(", ")}</p>}
                  {change.removed.length > 0 && <p>Lost: {change.removed.join(", ")}</p>}
                  {gains && (
                    <>
                      <p className="role-confirm__sensitive">
                        {members.length} users will gain: {change.added.join(", ")}
                      </p>
                      <Checkbox checked={understood} onChange={(event) => setUnderstood(event.target.checked)}>
                        I understand
                      </Checkbox>
                    </>
                  )}
                  {replacement.siteScoping === "whole" && (
                    <p className="role-confirm__sensitive">Site links will no longer limit these users</p>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </Modal>
  );
};

export default RoleDeleteModal;
